import { Injectable, ChangeDetectorRef } from '@angular/core';
import { Location } from '@angular/common';
import * as debug from './debug.service';
import { Breadcrumb } from 'src/app/shared/classes/breadcrumb';
import { AppSettings } from 'src/app/app.settings';
import { IUserProfile } from '../responses/iuser-profile';
import { IMenu, defaultMenuId, landingPageMenuId } from '../responses/imenu';
import {
	trimQueryString,
	getUrlSegments,
	getRootUrlSegment,
	getHomeUrl,
	isMobileWindow,
	fallbackMenu,
	filterMenuFromDbMenus,
	findMenuFromDbMenus,
} from '../utils/utility.common';
import { ProfileDbService } from './profile-db.service';
import { Subject } from 'rxjs';
import {
	ScrollToService,
	ScrollToConfigOptions,
} from '@nicky-lenaers/ngx-scroll-to';

@Injectable({ providedIn: 'root' })
export class MenuService {
	static isLoggedIn = false;
	static accessToken = '';

	private sidenavIsOpenedSubject = new Subject<boolean>();
	private isBusyGlobalSubject = new Subject<boolean>();

	sidenavIsOpenedChanged = this.sidenavIsOpenedSubject.asObservable();
	isBusyGlobalChanged = this.isBusyGlobalSubject.asObservable();

	origMenus: IMenu[];
	favoriteMenus: IMenu[];
	menus: IMenu[];
	activeModule: IMenu;
	activeRouteIsRoot: boolean;
	isBusyGlobal = false;
	isNetworkOnline = false; // TODO add UI indicator
	userProfile: IUserProfile;

	constructor(
		private readonly location: Location,
		private readonly appSettings: AppSettings,
		private readonly scrollToService: ScrollToService,
		// private changeDetectorRef: ChangeDetectorRef
	) {
		debug.log(`005 MenuService instantiated`);
	}

	// forceDetectChanges() {
	// 	this.changeDetectorRef.detectChanges();
	// }

	setActiveRouteIsRoot(url: string) {
		if (!url) {
			url = this.location.path();
		}

		const routes = url.split('/');

		this.activeRouteIsRoot = routes && routes.length <= 2;
	}

	initialize(userProfile: IUserProfile, caller: string) {
		debug.log(`${caller}->040 MenuService.initialize`);

		if (!userProfile) {
			debug.log(`${caller}->MenuService.initialize userProfile.menus: userProfile is empty. using fallbackMenu`, fallbackMenu);
			userProfile = fallbackMenu;
		}

		debug.log(`${caller}->MenuService.initialize userProfile:`, userProfile);

		this.userProfile = userProfile;
		this.origMenus = !!userProfile.menus ? userProfile.menus : [];
		this.favoriteMenus = this.origMenus.filter(
			(menu) => !menu.isHorizontalMenu && menu.allowInFavoritesNav
		);

		const url = this.location.path();
		const routerLink = url.toString().trim();

		this.appSettings.settings.fixedFooter = !this.userProfile.isActive;
		this.setActiveModule(
			routerLink,
			`${caller}->040 MenuService.initialize`
		);

		debug.log(`${caller}->MenuService.initialize userProfile.menus:`, userProfile.menus, true);
		debug.log(`${caller}->MenuService.initialize origMenus:`, this.origMenus, true);
		debug.log(`${caller}->MenuService.initialize favoriteMenus:`, this.favoriteMenus, true);
		debug.log(`${caller}->MenuService.initialize userProfile:`, this.userProfile);
	}

	setMenus() {
		debug.log(`042.1 MenuService - setMenus`);

		if (!this.activeModule) {
			debug.log(`042.2 MenuService - setMenus this.activeModule is empty exiting..`);

			return;
		}

		const parentId = this.activeModule.id;

		debug.log(`042.3 MenuService - setMenus origMenus:`, this.origMenus, true);

		if (isMobileWindow()) {
			// const allMenus = this.origMenus.filter(item => item.parentId !== defaultMenuId && item.parentId !== landingPageMenuId);
			const allMenus = this.origMenus.filter(
				(item) => item.parentId !== defaultMenuId
			);

			this.menus = !!allMenus ? allMenus : [];
		} else {
			const menus = this.origMenus.filter(
				(item) =>
					item.parentId === parentId &&
					(item.title !== 'Home' || item.forceShow === true)
			);

			this.menus = !!menus ? menus : [];
		}

		debug.log(`042.4 MenuService - setMenus menus:`, this.menus, true);
		// console.table(this.menus);
	}

	setActiveModule(routerLink: string, caller: string) {
		let urlNoQuerystring = trimQueryString(routerLink);

		debug.log(`${caller}->setActiveModule routerLink: ${urlNoQuerystring}`);

		urlNoQuerystring = urlNoQuerystring?.toString().trim();

		this.activeModule = this.getRootMenuModule(urlNoQuerystring);

		debug.log(`${caller}->setActiveModule activeModule:`, [this.activeModule], true);

		this.setMenus();
		this.setActiveRouteIsRoot(urlNoQuerystring);

		if (isMobileWindow()) {
			// this.appSettings.settings.menu = AppSettings.MenuOrientation.HORIZONTAL;
			this.sidenavIsOpenedSubject.next(false);
		} else {
			if (!this.activeModule) {
				debug.log(`037 MenuService setActiveModule this.activeModule is null exiting..`);

				return;
			}

			const isHorizontalMenu = !!this.activeModule.isHorizontalMenu;

			// this.appSettings.settings.menu = isHorizontalMenu ? AppSettings.MenuOrientation.HORIZONTAL : AppSettings.MenuOrientation.VERTICAL;
			debug.log(`038 MenuService setActiveModule isHorizontalMenu = ${isHorizontalMenu} this.activeModule.menuOrientation = ${this.activeModule.menuOrientation} this.appSettings.settings.sidenavIsOpened = ${this.appSettings.settings.sidenavIsOpened}`);

			this.sidenavIsOpenedSubject.next(!isHorizontalMenu);
		}
	}

	getActiveMenu(caller: string): IMenu {
		const url = this.location.path();
		const urlNoQuerystring = trimQueryString(url);
		const menus = this.getMenus(
			urlNoQuerystring,
			`${caller}->getActiveMenu`
		);

		if (!!menus && menus.length > 0) {
			const menu = menus[0];

			menu.header = menu.header ? menu.header : menu.title;

			debug.log(`${caller}->getActiveMenu returning menu:`, [menu], true);

			return menu;
		}

		debug.log(`${caller}->getActiveMenu menus not found. Returning undefined.`);

		return undefined;
	}

	getBreadcrumbs(caller: string): Breadcrumb[] {
		const activeMenu = this.getActiveMenu(`${caller}->getBreadcrumbs`);

		if (!activeMenu) {
			return undefined;
		}

		const urlSegments = getUrlSegments(activeMenu.routerLink);
		const breadcrumbs = [];

		if (isMobileWindow() && activeMenu.routerLink !== '/') {
			const home = new Breadcrumb();

			home.url = '/';
			home.name = 'Home';
			breadcrumbs.push(home as any);
		}

		let url = '';

		urlSegments.forEach((i: any) => {
			url = `${url}/${i}`;

			// const menu = this.origMenus.find(m => m.routerLink === url);
			const menu = findMenuFromDbMenus(url, this.origMenus);

			if (menu) {
				const breadcrumb = new Breadcrumb();

				breadcrumb.url = menu.routerLink;
				breadcrumb.name = menu.title;
				breadcrumbs.push(breadcrumb as any);
			}
		});

		return breadcrumbs;
	}

	// todo refactor to array
	getMenus(url: string, caller: string): IMenu[] {
		let targetUrl = url;

		if (!url) {
			targetUrl = '/';
		}

		if (this.origMenus) {
			// const menus = this.origMenus.filter(item => item.routerLink === targetUrl);
			const menus = filterMenuFromDbMenus(targetUrl, this.origMenus);

			if (menus.length > 1) {
				const menus2 = menus.find((item) => item.isRootModule);
				if (menus2) {
					debug.warn(`${caller}->getMenus found multiple items, expected one. Using root module. filtered menus:`, menus2);

					return [menus2];
				} else {
					debug.warn(`${caller}->getMenus found multiple items, expected one. Adjust the filter to ensure only one item is returned. filtered menus:`, menus2);
				}
			}

			if (menus.length === 0) {
				debug.warn(`${caller}->getMenus url: ${url} not found!!`);
			}

			return !!menus ? menus : [];
		}

		return [];
	}

	private getRootMenuModule(routerLink: string): IMenu {
		const url = getRootUrlSegment(routerLink);

		let targetUrl = url;

		if (!url) {
			targetUrl = '/';
		}
		if (targetUrl === '/signin-callback') {
			targetUrl = '/';
		}
		if (targetUrl === '/signout-callback') {
			targetUrl = '/';
		}
		if (isMobileWindow()) {
			targetUrl = '/root';
		}

		const menus = this.getMenus(
			targetUrl,
			`042.1 MenuService.getRootMenuModule`
		);

		let menu = menus.find(
			(item) => item.isRootModule || item.isHorizontalMenu
		);

		if (
			menu &&
			menu.isHorizontalMenu &&
			this.isNotHomeOrLandingMenu(menu.parentId)
		) {
			menu = this.origMenus.find((item) => item.id === menu.parentId);
		}

		if (!menu) {
			debug.warn(`042.2 MenuService - getRootMenuModule unable to find root module targetUrl: ${targetUrl} menus:`, menus);
		}

		return menu;
	}

	private isNotHomeOrLandingMenu(id: number): boolean {
		return id !== defaultMenuId && id !== landingPageMenuId;
	}

	expandActiveSubMenu(routerLink: string, caller: string) {
		debug.log(`${caller}->089 MenuService.expandActiveSubMenu`);

		if (!routerLink) {
			debug.log(`${caller}->089.1 MenuService.expandActiveSubMenu routerLink is null. getting this.location.path()`);
			routerLink = this.location.path();
		}

		this.setActiveRouteIsRoot(routerLink);

		if (!this.menus) {
			debug.log(`${caller}->089.2 MenuService.expandActiveSubMenu this.menus is null. exiting..`);
			return;
		}

		const urlSegments = getUrlSegments(routerLink);

		let url = '';

		if (urlSegments.length > 0) {
			debug.log(`${caller}->089.2 MenuService.expandActiveSubMenu urlSegments found:`, urlSegments);

			urlSegments.forEach((urlSegment) => {
				url = `${url}/${urlSegment}`;

				const menuItem = findMenuFromDbMenus(url, this.menus);

				if (menuItem) {
					if (menuItem.hasSubMenu) {
						debug.log(`${caller}->089.2 MenuService.expandActiveSubMenu menuItem menuItem.hasSubMenu = true expanding: ${menuItem.id}`, [menuItem]);

						this.expandMenu(menuItem.id);
					} else {
						if (this.isNotHomeOrLandingMenu(menuItem.parentId)) {
							debug.log(`${caller}->089.3 MenuService.expandActiveSubMenu menuItem menuItem.hasSubMenu = false menu parent isNotHomeOrLandingMenu menuItem:`, [menuItem]);

							const parentMenu = this.menus.find(
								(item) => item.id === menuItem.parentId
							);

							if (parentMenu) {
								debug.log(`${caller}->089.4 MenuService.expandActiveSubMenu parentMenu found: expanding parentMenu.id: ${parentMenu.id}`, [parentMenu]);

								this.expandMenu(parentMenu.id);
							} else {
								debug.log(`${caller}->089.5 MenuService.expandActiveSubMenu parentMenu NOT found no expansion here.`, [parentMenu]);
							}
						} else {
							debug.log(`${caller}->089.6 MenuService.expandActiveSubMenu menuItem menuItem.hasSubMenu = false menu parent IS HomeOrLandingMenu no expansion here. menuItem:`, [menuItem]);
						}
					}
				} else {
					debug.log(`${caller}->089.7 MenuService.expandActiveSubMenu menuItem NOT found for: ${url}`);
				}
			});
		} else {
			url = '/';
			debug.log(`${caller}->089.8 MenuService.expandActiveSubMenu routerLink not found: ${routerLink} using '${url}'`);

			const menus = this.getMenus(
				url,
				`${caller}->089.81 MenuService.expandActiveSubMenu`
			);
			const menuItem = menus.find((item) => item.isRootModule);

			if (menuItem) {
				if (menuItem.hasSubMenu) {
					debug.log(`${caller}->089.9 MenuService.expandActiveSubMenu menuItem found: expanding menuItem.id: ${menuItem.id}`, [menuItem]);

					this.expandMenu(menuItem.id);
				} else {
					debug.log(`${caller}->089.10 MenuService.expandActiveSubMenu menuItem found: menuItem.hasSubMenu = false. no expansion here.`, [menuItem]);
				}

			} else {
				debug.log(`${caller}->089.11 MenuService.expandActiveSubMenu menuItem NOT found url: ${url}' no expansion here.`);
			}
		}
	}

	expandMenu(menuId: number) {
		const menuItem = document.getElementById(`menu-item-${menuId}`);
		const subMenu = document.getElementById(`sub-menu-${menuId}`);

		if (subMenu) {
			subMenu.classList.add('show');
			menuItem.classList.add('expanded');
		}
	}

	isCurrentUrl(routerLink: string): boolean {
		const url = this.location.path();

		return url === routerLink;
	}

	toggleMenuItem(menuId: number) {
		const menuItem = document.getElementById(`menu-item-${menuId}`);
		const subMenu = document.getElementById(`sub-menu-${menuId}`);

		if (subMenu) {
			if (subMenu.classList.contains('show')) {
				subMenu.classList.remove('show');
				menuItem.classList.remove('expanded');
			} else {
				subMenu.classList.add('show');
				menuItem.classList.add('expanded');
			}
		}

		const selectedMenu = this.menus.find((item) => item.id === menuId);

		if (window.innerWidth <= 960) {
			if (!!selectedMenu && !selectedMenu.hasSubMenu) {
				// this.appSettings.settings.menu = AppSettings.MenuOrientation.HORIZONTAL;
				this.sidenavIsOpenedSubject.next(false);
			}
		}

		if (!!selectedMenu && !selectedMenu.hasSubMenu) {
			this.setActiveRouteIsRoot(selectedMenu.routerLink);
		}
	}

	closeOtherSubMenus(menu: Array<IMenu>, menuId: number) {
		const currentMenuItem = menu.filter((item) => item.id === menuId)[0];

		if (
			currentMenuItem.parentId === landingPageMenuId &&
			!currentMenuItem.target
		) {
			menu.forEach((item) => {
				if (item.id !== menuId) {
					const subMenu = document.getElementById(
						'sub-menu-' + item.id
					);
					const menuItem = document.getElementById(
						'menu-item-' + item.id
					);

					if (subMenu) {
						if (subMenu.classList.contains('show')) {
							subMenu.classList.remove('show');
							menuItem.classList.remove('expanded');
						}
					}
				}
			});
		}
	}

	closeAllSubMenus() {
		const menu = document.getElementById('vertical-menu');

		if (menu) {
			const rootMenu = menu.children[0];

			// tslint:disable-next-line: prefer-for-of
			for (let i = 0; i < rootMenu.children.length; i++) {
				const child = rootMenu.children[i];

				if (child) {
					if (child.children[0].classList.contains('expanded')) {
						child.children[0].classList.remove('expanded');
						child.children[1].classList.remove('show');
					}
				}
			}
		}
	}

	getHomeUrl(): string {
		return getHomeUrl(this.activeModule);
	}

	setIsBusy(isBusy: boolean) {
		this.isBusyGlobal = isBusy;
		this.isBusyGlobalSubject.next(isBusy);
	}

	scrollTo(id: string, caller: string) {
		debug.warn(`${caller}->MenuService.scrollTo: ${id}`);

		const config: ScrollToConfigOptions = {
			target: `${id}`,
			// duration: 300,
			// easing: 'easeOutElastic',
			// offset: 20
		};

		this.scrollToService.scrollTo(config).subscribe(
			(value) => {
				debug.warn(`${caller}->MenuService.scrollTo value: ${value}`);
			},
			(err) =>
				debug.warn(`${caller}->MenuService.scrollTo: ${id} error: ${err}`) // Error is caught and logged instead of thrown
		);
	}
}
